import { BoxShopItemEntity } from "@/modules/boxShop/entities/boxShopItemEntity"
import {
  BoxShopItemCategory,
  type BoxShopItemDto,
  type BoxShopItemInputDto,
} from "@/modules/boxShop/gateway/boxShopApiDto"
import { useServicesContext } from "@/modules/core/services/services.context"
import { DEFAULT_PAGE_SIZE } from "@/modules/utils/paginated"
import type { Page } from "@/modules/utils/paginated"
import { useMutation, useQueryClient, useSuspenseInfiniteQuery, useSuspenseQuery } from "@tanstack/react-query"
import { useMemo, useState } from "react"

export const BOX_SHOP_ITEMS_QUERY_KEY = "boxShopItems"

export function useBoxShopItemsCount() {
  const { boxShopApi } = useServicesContext()
  const { data } = useSuspenseQuery<Page<BoxShopItemDto>>({
    queryKey: [BOX_SHOP_ITEMS_QUERY_KEY],
    queryFn: async () => await boxShopApi.getItems(undefined, undefined, undefined, undefined),
  })
  return data.totalSize
}

export function useBoxShopItems({
  category,
  searchTerm,
  defaultPage,
  defaultPageSize,
}: {
  category?: BoxShopItemCategory
  searchTerm?: string
  defaultPage?: number
  defaultPageSize?: number
}) {
  const { boxShopApi } = useServicesContext()
  const [page, setPage] = useState(defaultPage ?? 0)
  const [pageSize, setPageSize] = useState(defaultPageSize ?? DEFAULT_PAGE_SIZE)

  const { data, isLoading, error } = useSuspenseQuery<Page<BoxShopItemDto>>({
    queryKey: [BOX_SHOP_ITEMS_QUERY_KEY, { page, pageSize, category, searchTerm }],
    queryFn: async () => {
      return await boxShopApi.getItems(category, searchTerm ?? undefined, page, pageSize)
    },
  })

  const handleSetPage = (newPageNumber: number) => {
    setPage(newPageNumber)
  }

  const handlePreviousPage = () => {
    if (data.page > 0) {
      setPage((prev) => prev - 1)
    }
  }

  const handleNextPage = () => {
    if (data.page < data.totalPages - 1) {
      setPage((prev) => prev + 1)
    }
  }

  return {
    data: data.content.map((item) => BoxShopItemEntity.fromDto(item)),
    isLoading,
    error,
    pageSize,
    setPageSize,
    handleSetPage,
    handlePreviousPage,
    handleNextPage,
    hasPreviousPage: data.page > 0,
    hasNextPage: data.page < data.totalPages - 1,
    totalElements: data.totalSize,
  }
}

export function useBoxShopItemsInfinite({
  category,
  searchTerm,
  pageSize = DEFAULT_PAGE_SIZE,
}: {
  category?: BoxShopItemCategory
  searchTerm?: string
  pageSize?: number
}) {
  const { boxShopApi } = useServicesContext()

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useSuspenseInfiniteQuery({
    queryKey: [BOX_SHOP_ITEMS_QUERY_KEY, "infinite", { category, searchTerm, pageSize }],
    queryFn: async ({ pageParam }) => {
      return await boxShopApi.getItems(category, searchTerm ?? undefined, pageParam, pageSize)
    },
    staleTime: 1000 * 60 * 5,
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      if (lastPage.content.length === 0) {
        return null
      }
      return lastPage.page + 1
    },
  })

  const items = useMemo(
    () => data.pages.flatMap((page) => page.content).map((item) => BoxShopItemEntity.fromDto(item)),
    [data],
  )

  return {
    data: items,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  }
}

export function useCreateBoxShopItem() {
  const { boxShopApi } = useServicesContext()
  const queryClient = useQueryClient()
  const { mutateAsync: createBoxShopItem, isPending: isCreateBoxShopItemLoading } = useMutation<
    BoxShopItemDto,
    Error,
    BoxShopItemInputDto
  >({
    mutationFn: async (input: BoxShopItemInputDto) => await boxShopApi.createItem(input),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [BOX_SHOP_ITEMS_QUERY_KEY] })
    },
  })
  return {
    createBoxShopItem,
    isCreateBoxShopItemLoading,
  }
}

export function useUpdateBoxShopItem() {
  const { boxShopApi } = useServicesContext()
  const queryClient = useQueryClient()
  const { mutateAsync: updateBoxShopItem, isPending: isUpdateBoxShopItemLoading } = useMutation<
    BoxShopItemDto,
    Error,
    { id: string; data: BoxShopItemInputDto }
  >({
    mutationFn: async ({ id, data }: { id: string; data: BoxShopItemInputDto }) =>
      await boxShopApi.updateItem(id, data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [BOX_SHOP_ITEMS_QUERY_KEY] })
    },
  })
  return {
    updateBoxShopItem,
    isUpdateBoxShopItemLoading,
  }
}

export function useDeleteBoxShopItem() {
  const { boxShopApi } = useServicesContext()
  const queryClient = useQueryClient()
  const { mutateAsync: deleteBoxShopItem, isPending: isDeleteBoxShopItemLoading } = useMutation({
    mutationFn: async (id: string) => await boxShopApi.deleteItem(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [BOX_SHOP_ITEMS_QUERY_KEY] })
    },
  })
  return {
    deleteBoxShopItem,
    isDeleteBoxShopItemLoading,
  }
}
